import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useParams } from 'react-router-dom';
import type { StudentPayload } from './checkInTypes';
import SignaturePad from './SignaturePad';

type PublicSchoolPayload = {
  id: string;
  schoolId: string;
  name: string;
  timezone: string;
  grades: string[];
  rooms: string[];
  requiresAttendanceCode: boolean;
};

type StudentLimitPayload = {
  studentCount: number;
  /** `null` means unlimited. */
  limit: number | null;
  planName: string | null;
  overLimit: boolean;
};

type SelectionState = {
  grade: string;
  room: string;
  studentId: string;
};

type AttendanceType = 'checkin' | 'checkout';

type TodayAttendanceRecord = {
  id: string;
  attendance: AttendanceType;
  by: string;
  at: string;
  signature: string | null;
};

type TodayAttendance = {
  day: string;
  checkin: TodayAttendanceRecord | null;
  checkout: TodayAttendanceRecord | null;
};

type AttendanceCodeStatus = 'idle' | 'checking' | 'valid' | 'invalid';

const selectClassName =
  'mt-1 block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60';

const storageKeyFor = (publicSchoolId: string) =>
  `ontimecheckin:attendance-selection:${publicSchoolId}`;

const readStoredSelection = (publicSchoolId: string): SelectionState | null => {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(publicSchoolId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SelectionState>;
    return {
      grade: typeof parsed.grade === 'string' ? parsed.grade : '',
      room: typeof parsed.room === 'string' ? parsed.room : '',
      studentId: typeof parsed.studentId === 'string' ? parsed.studentId : '',
    };
  } catch {
    return null;
  }
};

const writeStoredSelection = (publicSchoolId: string, selection: SelectionState) => {
  try {
    window.localStorage.setItem(storageKeyFor(publicSchoolId), JSON.stringify(selection));
  } catch {
    // Ignore storage failures (private mode / quota).
  }
};

const fullName = (student: StudentPayload) =>
  [student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ');

const formatAttendanceTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

type AttendanceSummaryCardProps = {
  label: string;
  emptyLabel: string;
  loading: boolean;
  record: TodayAttendanceRecord | null;
};

const AttendanceSummaryCard = ({
  label,
  emptyLabel,
  loading,
  record,
}: AttendanceSummaryCardProps) => (
  <div className="rounded-xl border border-ink/10 bg-white px-3 py-3">
    <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">{label}</p>
    {loading ? (
      <p className="mt-1 font-sans text-sm text-ink/55">Loading…</p>
    ) : record ? (
      <>
        <p className="mt-1 font-sans text-sm font-semibold text-ink">
          {formatAttendanceTime(record.at)}
        </p>
        <p className="mt-0.5 font-sans text-xs text-ink/60">by {record.by}</p>
        {record.signature ? (
          <figure className="mt-2">
            <img
              src={record.signature}
              alt={`Signature for ${label.toLowerCase()} by ${record.by}`}
              className="h-16 w-full rounded-lg border border-ink/10 bg-white object-contain"
            />
            <figcaption className="mt-1 font-sans text-[0.65rem] uppercase tracking-wide text-ink/45">
              Signature
            </figcaption>
          </figure>
        ) : (
          <p className="mt-2 font-sans text-xs text-ink/45">No signature on file</p>
        )}
      </>
    ) : (
      <p className="mt-1 font-sans text-sm text-ink/55">{emptyLabel}</p>
    )}
  </div>
);

const AttendancePage = () => {
  const { schoolId: publicSchoolIdParam } = useParams<{ schoolId: string }>();
  const publicSchoolId = (publicSchoolIdParam ?? '').trim();

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [school, setSchool] = useState<PublicSchoolPayload | null>(null);
  const [students, setStudents] = useState<StudentPayload[]>([]);
  const [studentLimit, setStudentLimit] = useState<StudentLimitPayload | null>(null);
  const [gradeFilter, setGradeFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [by, setBy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectionHydrated, setSelectionHydrated] = useState(false);
  const [todayStatus, setTodayStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance | null>(null);
  const [todayError, setTodayError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<AttendanceType | null>(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [attendanceCodeInput, setAttendanceCodeInput] = useState('');
  const [attendanceCodeStatus, setAttendanceCodeStatus] =
    useState<AttendanceCodeStatus>('idle');

  const requiresAttendanceCode = Boolean(school?.requiresAttendanceCode);
  const attendanceBlocked = Boolean(studentLimit?.overLimit);

  const load = useCallback(async () => {
    if (!publicSchoolId) {
      setError('School ID is missing from the URL.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setError(null);
    try {
      const res = await fetch(`/api/attendance/${encodeURIComponent(publicSchoolId)}`);
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Failed to load attendance page (${res.status})`);
      }
      const data = (await res.json()) as {
        school: PublicSchoolPayload;
        students: StudentPayload[];
        studentLimit?: StudentLimitPayload;
      };
      setSchool(data.school);
      setStudents(data.students);
      setStudentLimit(data.studentLimit ?? null);
      if (data.studentLimit?.overLimit) {
        setSelectedStudentId('');
      }
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setStatus('error');
    }
  }, [publicSchoolId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (status !== 'ready' || !publicSchoolId || selectionHydrated) return;
    const stored = readStoredSelection(publicSchoolId);
    if (stored) {
      const gradeOk = !stored.grade || school?.grades.includes(stored.grade);
      const roomOk = !stored.room || school?.rooms.includes(stored.room);
      const studentOk =
        !stored.studentId || students.some((student) => student.id === stored.studentId);
      setGradeFilter(gradeOk ? stored.grade : '');
      setRoomFilter(roomOk ? stored.room : '');
      setSelectedStudentId(studentOk ? stored.studentId : '');
    }
    setSelectionHydrated(true);
  }, [publicSchoolId, school, selectionHydrated, status, students]);

  useEffect(() => {
    if (!selectionHydrated || !publicSchoolId) return;
    writeStoredSelection(publicSchoolId, {
      grade: gradeFilter,
      room: roomFilter,
      studentId: selectedStudentId,
    });
  }, [gradeFilter, publicSchoolId, roomFilter, selectedStudentId, selectionHydrated]);

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      if (gradeFilter && student.grade !== gradeFilter) return false;
      if (roomFilter && student.room !== roomFilter) return false;
      return true;
    });
  }, [gradeFilter, roomFilter, students]);

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === selectedStudentId) ?? null,
    [selectedStudentId, students],
  );

  useEffect(() => {
    if (!selectedStudentId) return;
    const stillVisible = filteredStudents.some((student) => student.id === selectedStudentId);
    if (!stillVisible) {
      setSelectedStudentId('');
    }
  }, [filteredStudents, selectedStudentId]);

  useEffect(() => {
    if (!selectedStudent) {
      setBy('');
      setTodayAttendance(null);
      setTodayStatus('idle');
      setTodayError(null);
      setConfirmAction(null);
      setSignatureDataUrl(null);
      setAttendanceCodeInput('');
      setAttendanceCodeStatus('idle');
      return;
    }
    setBy(selectedStudent.parent_guardian_1 || '');
    setSubmitError(null);
    setSuccessMessage(null);
    setConfirmAction(null);
    setSignatureDataUrl(null);
    setAttendanceCodeInput('');
    setAttendanceCodeStatus('idle');
  }, [selectedStudent]);

  const loadTodayAttendance = useCallback(async (studentId: string) => {
    if (!publicSchoolId) return;
    setTodayStatus('loading');
    setTodayError(null);
    try {
      const res = await fetch(
        `/api/attendance/${encodeURIComponent(publicSchoolId)}/students/${encodeURIComponent(studentId)}/today`,
      );
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Failed to load today's attendance (${res.status})`);
      }
      const data = (await res.json()) as TodayAttendance;
      setTodayAttendance(data);
      setTodayStatus('ready');
    } catch (e) {
      setTodayAttendance(null);
      setTodayError(e instanceof Error ? e.message : "Failed to load today's attendance");
      setTodayStatus('error');
    }
  }, [publicSchoolId]);

  useEffect(() => {
    if (!selectedStudent) return;
    void loadTodayAttendance(selectedStudent.id);
  }, [loadTodayAttendance, selectedStudent]);

  useEffect(() => {
    if (confirmAction === null || !requiresAttendanceCode || !publicSchoolId) {
      return;
    }

    const trimmed = attendanceCodeInput.trim();
    if (!trimmed) {
      setAttendanceCodeStatus('idle');
      return;
    }

    // Clear previous result on each keystroke; validate only after typing pauses.
    setAttendanceCodeStatus('idle');

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setAttendanceCodeStatus('checking');
      void (async () => {
        try {
          const res = await fetch(
            `/api/attendance/${encodeURIComponent(publicSchoolId)}/validate-code`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: trimmed }),
            },
          );
          if (!res.ok) {
            throw new Error(`Failed to validate code (${res.status})`);
          }
          const data = (await res.json()) as { valid?: boolean };
          if (cancelled) return;
          setAttendanceCodeStatus(data.valid ? 'valid' : 'invalid');
        } catch {
          if (cancelled) return;
          setAttendanceCodeStatus('invalid');
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [attendanceCodeInput, confirmAction, publicSchoolId, requiresAttendanceCode]);

  const closeConfirm = () => {
    if (submitting) return;
    setConfirmAction(null);
    setSignatureDataUrl(null);
    setAttendanceCodeInput('');
    setAttendanceCodeStatus('idle');
  };

  const requestConfirm = (attendance: AttendanceType) => {
    if (attendanceBlocked) {
      setSubmitError(
        studentLimit && studentLimit.limit !== null
          ? `Check-in and check-out are unavailable. This school has ${studentLimit.studentCount} students but the current plan allows ${studentLimit.limit}. Please contact the school.`
          : 'Check-in and check-out are unavailable. Please contact the school.',
      );
      return;
    }
    if (!selectedStudent) return;
    if (!by.trim()) {
      setSubmitError('Please enter who is checking in or out.');
      return;
    }
    setSubmitError(null);
    setSuccessMessage(null);
    setSignatureDataUrl(null);
    setAttendanceCodeInput('');
    setAttendanceCodeStatus('idle');
    setConfirmAction(attendance);
  };

  const codeBlocksSubmit =
    requiresAttendanceCode && attendanceCodeStatus !== 'valid';

  const handleConfirmSubmit = async () => {
    if (!publicSchoolId || !selectedStudent || !confirmAction) return;
    if (attendanceBlocked) {
      setSubmitError(
        'Check-in and check-out are unavailable. Please contact the school.',
      );
      return;
    }
    if (!signatureDataUrl) {
      setSubmitError('Please provide a signature.');
      return;
    }
    if (codeBlocksSubmit) {
      setSubmitError('Please enter the correct attendance code.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSuccessMessage(null);
    try {
      const res = await fetch(`/api/attendance/${encodeURIComponent(publicSchoolId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId: selectedStudent.id,
          attendance: confirmAction,
          by: by.trim(),
          data: { signature: signatureDataUrl },
          ...(requiresAttendanceCode
            ? { attendanceCode: attendanceCodeInput.trim() }
            : {}),
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Submit failed (${res.status})`);
      }
      setSuccessMessage(
        confirmAction === 'checkin'
          ? `${fullName(selectedStudent)} checked in.`
          : `${fullName(selectedStudent)} checked out.`,
      );
      setConfirmAction(null);
      setSignatureDataUrl(null);
      setAttendanceCodeInput('');
      setAttendanceCodeStatus('idle');
      await loadTodayAttendance(selectedStudent.id);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <main className="relative z-0 mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <p className="font-sans text-sm text-ink/60">Loading attendance…</p>
      </main>
    );
  }

  if (status === 'error' || !school) {
    return (
      <main className="relative z-0 mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
          <h1 className="font-display text-2xl font-semibold text-ink">Attendance</h1>
          <p
            className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
            role="alert"
          >
            {error ?? 'School not found'}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft"
          >
            Try again
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="relative z-0 mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <p className="inline-flex rounded-full bg-skysoft/50 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-ink/70">
          Attendance
        </p>
        <h1 className="mt-3 font-display text-3xl font-semibold text-ink">{school.name}</h1>
        {attendanceBlocked && studentLimit ? (
          <p
            className="mt-4 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
            role="alert"
          >
            Check-in and check-out are unavailable. This school has{' '}
            {studentLimit.studentCount} students
            {studentLimit.limit !== null
              ? `, but the current plan allows ${studentLimit.limit}`
              : ''}
            . Please contact the school.
          </p>
        ) : null}
        {!selectedStudent ? (
          <>
            <p className="mt-2 font-sans text-sm text-ink/65">
              {attendanceBlocked
                ? 'Student list is view-only until the school resolves its plan limit.'
                : 'Filter by grade or room, then select a student to check in or check out.'}
            </p>

            <div className="mt-6 space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                    Grade
                  </span>
                  <select
                    className={selectClassName}
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                  >
                    <option value="">All grades</option>
                    {school.grades.map((grade) => (
                      <option key={grade} value={grade}>
                        {grade}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                    Room
                  </span>
                  <select
                    className={selectClassName}
                    value={roomFilter}
                    onChange={(e) => setRoomFilter(e.target.value)}
                  >
                    <option value="">All rooms</option>
                    {school.rooms.map((room) => (
                      <option key={room} value={room}>
                        {room}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div
                className={`max-h-[50vh] overflow-auto rounded-2xl border-2 border-ink/10 ${
                  attendanceBlocked ? 'opacity-60' : ''
                }`}
              >
                <table className="min-w-full divide-y divide-ink/10 text-left">
                  <thead className="sticky top-0 bg-paper/95">
                    <tr className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                      <th className="px-4 py-3">Student</th>
                      <th className="px-4 py-3">Grade</th>
                      <th className="px-4 py-3">Room</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/10">
                    {filteredStudents.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-8 text-center font-sans text-sm text-ink/55"
                        >
                          No students match the current filters.
                        </td>
                      </tr>
                    ) : (
                      filteredStudents.map((student) => (
                        <tr
                          key={student.id}
                          className={
                            attendanceBlocked
                              ? 'font-sans text-sm text-ink'
                              : 'cursor-pointer font-sans text-sm text-ink transition hover:bg-skysoft/20'
                          }
                          onClick={() => {
                            if (attendanceBlocked) return;
                            setSelectedStudentId(student.id);
                          }}
                          aria-disabled={attendanceBlocked}
                        >
                          <td className="px-4 py-3 font-semibold">{fullName(student)}</td>
                          <td className="px-4 py-3">{student.grade || '—'}</td>
                          <td className="px-4 py-3">{student.room || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <p className="font-sans text-sm text-ink/60">
                {filteredStudents.length} student{filteredStudents.length === 1 ? '' : 's'}
              </p>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setSelectedStudentId('')}
            className="mt-4 flex w-full items-center justify-between gap-3 rounded-2xl border-2 border-ink/10 bg-paper/40 px-4 py-3 text-left transition hover:bg-skysoft/20"
          >
            <div className="min-w-0">
              <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                Student list collapsed
              </p>
              <p className="mt-1 truncate font-sans text-sm font-semibold text-ink">
                {[gradeFilter || 'All grades', roomFilter || 'All rooms'].join(' · ')}
                {' · '}
                {filteredStudents.length} student
                {filteredStudents.length === 1 ? '' : 's'}
              </p>
            </div>
            <span className="shrink-0 font-sans text-sm font-semibold text-ink/70">
              Change ▾
            </span>
          </button>
        )}

        {selectedStudent ? (
          <div className="mt-5 space-y-4 rounded-2xl border-2 border-ink/10 bg-paper/40 p-5">
            <div>
              <h2 className="font-display text-xl font-semibold text-ink">
                {fullName(selectedStudent)}
              </h2>
              <p className="mt-1 font-sans text-sm text-ink/65">
                {[selectedStudent.grade, selectedStudent.room].filter(Boolean).join(' · ') ||
                  'No grade/room set'}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <AttendanceSummaryCard
                label="Check-in today"
                emptyLabel="Not checked in yet"
                loading={todayStatus === 'loading'}
                record={todayAttendance?.checkin ?? null}
              />
              <AttendanceSummaryCard
                label="Check-out today"
                emptyLabel="Not checked out yet"
                loading={todayStatus === 'loading'}
                record={todayAttendance?.checkout ?? null}
              />
            </div>

            {todayError ? (
              <p
                className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                role="alert"
              >
                {todayError}
              </p>
            ) : null}

            <label className="block max-w-md">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                Checked in / out by
              </span>
              <input
                type="text"
                className={selectClassName}
                value={by}
                onChange={(e) => {
                  setBy(e.target.value);
                  setSubmitError(null);
                  setSuccessMessage(null);
                }}
                placeholder="Parent / guardian name"
                list={`guardian-options-${selectedStudent.id}`}
              />
              <datalist id={`guardian-options-${selectedStudent.id}`}>
                {[selectedStudent.parent_guardian_1, selectedStudent.parent_guardian_2]
                  .filter(Boolean)
                  .map((name) => (
                    <option key={name} value={name} />
                  ))}
              </datalist>
            </label>

            {submitError ? (
              <p
                className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                role="alert"
              >
                {submitError}
              </p>
            ) : null}

            {successMessage ? (
              <p
                className="rounded-xl bg-grass/30 px-3 py-2 font-sans text-sm font-semibold text-ink"
                role="status"
              >
                {successMessage}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={submitting || attendanceBlocked}
                onClick={() => requestConfirm('checkin')}
                className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
              >
                Check in
              </button>
              <button
                type="button"
                disabled={submitting || attendanceBlocked}
                onClick={() => requestConfirm('checkout')}
                className="rounded-full bg-grass/50 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-grass/70 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Check out
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <Transition appear show={confirmAction !== null} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={closeConfirm}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                  <Dialog.Title className="font-display text-xl font-semibold text-ink">
                    {confirmAction === 'checkout' ? 'Confirm check-out?' : 'Confirm check-in?'}
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 font-sans text-sm text-ink/70">
                    {selectedStudent ? (
                      <>
                        {confirmAction === 'checkout' ? 'Check out' : 'Check in'}{' '}
                        <span className="font-semibold text-ink">
                          {fullName(selectedStudent)}
                        </span>
                        {by.trim() ? (
                          <>
                            {' '}
                            by <span className="font-semibold text-ink">{by.trim()}</span>
                          </>
                        ) : null}
                        . Please sign below to confirm.
                      </>
                    ) : null}
                  </Dialog.Description>

                  {requiresAttendanceCode ? (
                    <div className="mt-5">
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Attendance code
                        </span>
                        <p className="mt-1 font-sans text-sm text-ink/65">
                          If you do not know the code, please contact school staff.
                        </p>
                        <div className="relative mt-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d{4}"
                            maxLength={4}
                            disabled={submitting}
                            value={attendanceCodeInput}
                            onChange={(event) => {
                              const next = event.target.value.replace(/\D/g, '').slice(0, 4);
                              setAttendanceCodeInput(next);
                              setSubmitError(null);
                            }}
                            placeholder="Enter confirmation code"
                            className={`${selectClassName} pr-10 tracking-[0.2em]`}
                            autoComplete="one-time-code"
                          />
                          {attendanceCodeStatus === 'valid' ? (
                            <CheckCircleIcon
                              className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-emerald-600"
                              aria-hidden
                            />
                          ) : null}
                        </div>
                      </label>
                      {attendanceCodeStatus === 'checking' ? (
                        <p className="mt-2 font-sans text-sm text-ink/55" role="status">
                          Checking code…
                        </p>
                      ) : null}
                      {attendanceCodeStatus === 'invalid' ? (
                        <p
                          className="mt-2 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                          role="alert"
                        >
                          That code is incorrect. Please try again.
                        </p>
                      ) : null}
                      {attendanceCodeStatus === 'valid' ? (
                        <p
                          className="mt-2 font-sans text-sm font-semibold text-emerald-700"
                          role="status"
                        >
                          Code accepted
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="mt-5">
                    <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                      Signature
                    </p>
                    {confirmAction ? (
                      <SignaturePad
                        key={confirmAction}
                        disabled={submitting}
                        onChange={setSignatureDataUrl}
                      />
                    ) : null}
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={closeConfirm}
                      className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={submitting || !signatureDataUrl || codeBlocksSubmit}
                      onClick={() => void handleConfirmSubmit()}
                      className="rounded-full bg-skysoft/70 px-5 py-2 text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft disabled:opacity-50"
                    >
                      {submitting
                        ? confirmAction === 'checkout'
                          ? 'Checking out…'
                          : 'Checking in…'
                        : confirmAction === 'checkout'
                          ? 'Confirm check-out'
                          : 'Confirm check-in'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </main>
  );
};

export default AttendancePage;
